import { useState } from "react";
import {
  Card,
  Table,
  Paper,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Avatar,
  Stack,
  Chip,
  Link,
} from "@mui/material";
import Iconify from "./iconify";
import Scrollbar from "./scrollbar";
import { formatCurrency } from "src/utils/clubChallengesUtils";
import { getUserPermissions } from "src/utils/permissionsUtils";
import useWithUrlTenant from "src/hooks/useTenant";

export default function SetupsTable({
  loading,
  rows,
  onEdit,
  onViewChallenges,
  onDelete,
  emptyMessage = "No Setup Found",
  deleteText = "Delete Setup",
}) {
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [clubData, setClubData] = useState(null);
  const { isViewer } = getUserPermissions();
  const withTenant = useWithUrlTenant();
  
  const getChallengeTypeChip = (type) => {
    const typeConfig = {
      0: {
        bgColor: "#2196f3",
        textColor: "#fff",
        label: "Club",
        icon: "eva:people-fill"
      },
      1: {
        bgColor: "#ff9800",
        textColor: "#fff",
        label: "Public",
        icon: "eva:globe-2-fill"
      },
      2: {
        bgColor: "#BF40BF",
        textColor: "#fff",
        label: "Private",
        icon: "eva:lock-fill"
      },
    };

    const config = typeConfig[type] || {
      bgColor: "#9e9e9e",
      textColor: "#fff",
      label: type,
      icon: "eva:question-mark-circle-fill"
    };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: config.bgColor,
          color: config.textColor,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          minWidth: 'fit-content',
          width: 'fit-content',
        }}
      >
        <Iconify
          icon={config.icon}
          sx={{
            width: 14,
            height: 14,
          }}
        />
        {config.label}
      </Box>
    );
  };

  const handleOpenMenu = (event, data) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
    setClubData(data);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const formatCurrencyDisplay = (value) => {
    if (!value) return "--";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <img
          src="/assets/icons/ufcoin.svg"
          alt="coin"
          style={{ width: 16, height: 16 }}
        />
        {formatCurrency(value)}
      </Box>
    );
  };

  const calculateAvgEntries = (totalEntries, noOfChallenges) => {
    if (!totalEntries || !noOfChallenges) {
      return "--";
    }
    return (totalEntries / noOfChallenges).toFixed(1);
  };

  const calculateProfitMargin = (totalEntryFee, totalPrizePoolSize) => {
    if (!totalEntryFee || !totalPrizePoolSize) {
      return "--";
    }
    const calculate = (
      ((totalEntryFee - totalPrizePoolSize) / totalEntryFee) *
      100
    ).toFixed(1);
    return `${calculate} %`;
  };

  const handleEdit = () => {
    handleCloseMenu();
    if (onEdit) {
      onEdit(openId, clubData);
    }
  };

  const handleViewChallenges = () => {
    handleCloseMenu();
    if (onViewChallenges) {
      onViewChallenges(openId);
    }
  };

  const handleDelete = () => {
    handleCloseMenu();
    if (onDelete) {
      onDelete(openId);
    }
  };

  return (
    <>
      <Card>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows?.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 4,
              color: "text.secondary",
            }}
          >
            <Iconify
              icon="eva:inbox-outline"
              sx={{ fontSize: 64, mb: 2, opacity: 0.5 }}
            />
            <Typography variant="h6" gutterBottom>
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <Scrollbar>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="club challenges table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Club</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Auto</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payout</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Users</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Creator</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 80 }}>Total Entries</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 80 }}>Avg. Entries</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 80 }}>Entry Fees</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 80 }}>Prize Pool</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 80 }}>Profit</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 80 }}>Margin</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 80 }}>Fill %</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600 }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows?.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": { backgroundColor: "#f8f9fa" },
                      }}
                    >
                      <TableCell>{row.clubId}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            src={row?.iconUrl}
                            alt={row?.iconUrl}
                            sx={{ width: 32, height: 32 }}
                          />
                          <Typography variant="body2">
                            {row?.clubName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {getChallengeTypeChip(row?.type)}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Iconify
                            icon={
                              row.isAutoGeneration
                                ? "eva:checkmark-circle-fill"
                                : "eva:close-circle-fill"
                            }
                            sx={{
                              fontSize: 20,
                              color: row.isAutoGeneration ? "#4CAF50" : "#f44336",
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.payoutFormat}
                          size="small"
                          sx={{
                            backgroundColor: "#4CAF50",
                            color: "white",
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {row?.clubTotals?.totalUserCount ?? "--"}
                      </TableCell>
                      <TableCell>
                        {row?.createdBy ? ( 
                        <Link 
                          href={withTenant(`/dashboard/profile?user_id=${row?.createdBy}`)}
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          { row?.createdByUsername.toString().length > 10
                            ? `${row?.createdByUsername.toString().slice(0, 10)}...`
                            : row?.createdByUsername.toString() ?? "N/A"}
                        </Link>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {row?.clubTotals?.totalEntry ?? "--"}
                      </TableCell>
                      <TableCell>
                        {calculateAvgEntries(
                          row?.clubTotals?.totalEntry,
                          row?.clubTotals?.totalChallengeCount
                        ) ?? "--"}
                      </TableCell>
                      <TableCell>
                        {formatCurrencyDisplay(row?.clubTotals?.totalEntryFee)}
                      </TableCell>
                      <TableCell>
                        {formatCurrencyDisplay(
                          row?.clubTotals?.totalPrizePoolSize
                        )}
                      </TableCell>
                      <TableCell>
                        {formatCurrencyDisplay(
                          row?.clubTotals?.totalEntryFee -
                          row?.clubTotals?.totalPrizePoolSize
                        )}
                      </TableCell>
                      <TableCell>
                        {calculateProfitMargin(
                          row?.clubTotals?.totalEntryFee,
                          row?.clubTotals?.totalPrizePoolSize
                        ) ?? "--"}
                      </TableCell>
                      <TableCell>
                        {row?.clubTotals?.averageFillPercentage !== null
                          ? row?.clubTotals?.averageFillPercentage + " %"
                          : "--"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="large"
                          color="inherit"
                          onClick={(e) => handleOpenMenu(e, row)}
                          id={row.clubId}
                        >
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        )}
      </Card>

      {/* Actions Menu */}
      {!isViewer && (
        <Popover
          open={Boolean(open)}
          anchorEl={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              p: 1,
              "& .MuiMenuItem-root": {
                px: 1,
                typography: "body2",
                borderRadius: 0.75,
              },
            },
          }}
        >
          <MenuItem onClick={handleEdit}>
            <Iconify icon="eva:edit-outline" sx={{ mr: 2 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleViewChallenges}>
            <Iconify icon="eva:eye-outline" sx={{ mr: 2 }} />
            View All Challenges
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />
            {deleteText}
          </MenuItem>
        </Popover>
      )}
    </>
  );
}
